/**
 * @file CorporateDetail
 * @description
 * Corporate detail form component that displays and manages corporate information.
 * It allows users to view or edit company details such as code, address, contact info,
 * registration numbers, and styled content through a TinyMCE editor.
 */

import React, { useRef, useEffect, useState } from 'react';
import { Form, Row, Col, Select } from 'antd';
import { Editor } from '@tinymce/tinymce-react';
import { InputComponent, CountryPhoneInput, useTranslation, ReferenceSelect } from 'soxo-bootstrap-core';
import { Referrers } from '../../models';

const { Option } = Select;

/**
 * @component CorporateDetail
 * @description
 * Displays a detailed form for entering or viewing corporate details.
 * The form supports field validation, multilingual labels, and rich-text editing using TinyMCE.
 *
 * @param {Object} props - Component props
 * @param {Object} props.initialValues - Pre-filled data to initialize the form fields
 * @param {Function} props.onValuesChange - Callback fired whenever a form field value changes
 */

const CorporateDetail = ({ initialValues, onValuesChange }) => {
    //To disable all fields on view
    const [viewDisable, setViewDisable] = useState(false);
    // store tinymc
    const [content, setContent] = useState('');
    const { t, i18n } = useTranslation(); // To Translate to another language
    const [form] = Form.useForm();
    // Reference to TinyMCE editor
    const editorRef = useRef(null);

    // Populate form fields when initialValues change
    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue({
                ...initialValues,
                cc_styledcontent: initialValues?.cc_styledcontent || '',
            });
        }
    }, [initialValues, form]);

    /**
     * @function handleUpdate
     * @description
     * Updates form field and component state when TinyMCE content changes.
     * Ensures updates occur only when the editor is focused.
     *
     * @param {string} value - Current editor value
     * @param {Object} editor - TinyMCE editor instance
     */

    const handleUpdate = (value, editor) => {
        const editorContent = editor.getContent();

        // Only update when editor has focus (avoids overwriting from external set)
        if (editor.hasFocus()) {
            setContent(editorContent);

            // Update form field so it's synced
            form.setFieldsValue({
                cc_styledcontent: editorContent,
            });
        }
    };

    /**
     * @function handleInit
     * @description
     * Initializes TinyMCE editor with initial content.
     *
     * @param {Event} evt - Initialization event
     * @param {Object} editor - TinyMCE editor instance
     */
    const handleInit = (evt, editor) => {
        const editorContent = editor.getContent();

        setContent(editorContent);
    };

    return (
        <Form
            form={form}
            layout="vertical"
            // initialValues={{ ...initialValues, cc_mode: ccMode }}
            onValuesChange={onValuesChange} //  Automatically sync to parent
            onFinish={null}
        >
            <Row gutter={20}>
                <Col xs={6}>
                    <Form.Item
                        name="cc_code"
                        label={t('Code')}
                        // rules={validationProps} hasFeedback
                    >
                        <InputComponent placeholder={t('Enter Code')} disabled={viewDisable} />
                    </Form.Item>
                </Col>

                <Col xs={6}>
                    <Form.Item name="cc_mode" label={t('Mode')}>
                        <InputComponent placeholder={t('Enter Mode')} c />
                    </Form.Item>
                </Col>

                <Col xs={6}>
                    <Form.Item
                        name="cc_place"
                        label={t('Place')}
                        rules={[
                            { max: 50, message: t('Length cannot exceed 50 characters!') },
                            { pattern: /^[ A-Za-z0-9_.,()&-]*$/, message: t('Enter Valid Place!') },
                        ]}
                    >
                        <InputComponent placeholder={t('Enter Place')} disabled={viewDisable} />
                    </Form.Item>
                </Col>

                <Col xs={6}>
                    <Form.Item
                        name="cc_zip"
                        label={t('Zip Code')}
                        rules={[
                            { max: 10, message: t('Length cannot exceed 10 characters!') },
                            { pattern: /(^\d{6,7}$)|(^\d{5}-\d{4}$)/, message: t('Enter Valid Zip Code!') },
                        ]}
                    >
                        <InputComponent placeholder={t('Enter Zip Code')} disabled={viewDisable} />
                    </Form.Item>
                </Col>
            </Row>

            {/* ===== Row 2: Company Name ===== */}
            <Row gutter={20}>
                <Col xs={24}>
                    <Form.Item
                        name="cc_desc"
                        label={t('Company Name')}
                        rules={[
                            { required: true, message: t('Please Enter Company Name!') },
                            { max: 50, message: t('Length cannot exceed 50 characters!') },
                        ]}
                    >
                        <InputComponent placeholder={t('Enter Company Name')} disabled={viewDisable} />
                    </Form.Item>
                </Col>
            </Row>

            {/* ===== Row 3: Company Address 1 & 2 ===== */}
            <Row gutter={20}>
                <Col xs={12}>
                    <Form.Item
                        name="cc_add1"
                        label={t('Company Address 1')}
                        rules={[
                            { max: 100, message: t('Length cannot exceed 100 characters!') },
                            { pattern: /^[ A-Za-z0-9_.,()&-]*$/, message: t('Enter Valid Address!') },
                        ]}
                    >
                        <InputComponent placeholder={t('Enter Company Address')} disabled={viewDisable} />
                    </Form.Item>
                </Col>

                <Col xs={12}>
                    <Form.Item
                        name="cc_add2"
                        label={t('Company Address 2')}
                        rules={[
                            { max: 100, message: t('Length cannot exceed 100 characters!') },
                            { pattern: /^[ A-Za-z0-9_.,()&-]*$/, message: t('Enter Valid Address!') },
                        ]}
                    >
                        <InputComponent placeholder={t('Enter Company Address')} disabled={viewDisable} />
                    </Form.Item>
                </Col>
            </Row>

            {/* ===== Row 4: Mobile, Email, Reg No.1, Reg No.2 ===== */}
            <Row gutter={20}>
                <Col xs={6}>
                    <Form.Item
                        name="cc_phone"
                        label={t('Mobile Number')}
                        rules={[{ pattern: /^[\d+ ]+$/, message: t('Enter Valid Mobile Number!') }]}
                    >
                        <CountryPhoneInput disabled={viewDisable} defaultCountryCode={process.env.REACT_APP_COUNTRYCODE || 'in'} />
                    </Form.Item>
                </Col>

                <Col xs={6}>
                    <Form.Item
                        name="cc_email"
                        label={t('Enter Company Mail ID')}
                        rules={[
                            { type: 'email', message: t('Enter Valid Email!') },
                            { max: 100, message: t('Length cannot exceed 100 characters!') },
                        ]}
                    >
                        <InputComponent placeholder={t('Enter Email ID')} disabled={viewDisable} />
                    </Form.Item>
                </Col>

                <Col xs={6}>
                    <Form.Item name="cc_regno1" label={t('Reg No. 1')} rules={[{ pattern: /^[a-zA-Z0-9]*$/, message: t('Enter Valid Reg No!') }]}>
                        <InputComponent placeholder={t('Enter Register No.1')} disabled={viewDisable} />
                    </Form.Item>
                </Col>

                <Col xs={6}>
                    <Form.Item name="cc_regno2" label={t('Reg No. 2')} rules={[{ pattern: /^[a-zA-Z0-9]*$/, message: t('Enter Valid Reg No!') }]}>
                        <InputComponent placeholder={t('Enter Register No.2')} disabled={viewDisable} />
                    </Form.Item>
                </Col>
            </Row>

            {/* ===== Row 5: Account Code, SI No, Customer Type, Active ===== */}
            <Row gutter={20}>
                <Col xs={6}>
                    <Form.Item
                        name="cc_acpostcode"
                        label={t('Account Code')}
                        rules={[{ max: 25, message: t('Length cannot exceed 25 characters!') }]}
                    >
                        <InputComponent placeholder={t('Enter Account Code')} disabled={viewDisable} />
                    </Form.Item>
                </Col>

                <Col xs={6}>
                    <Form.Item name="cc_slno" label={t('SI No')} rules={[{ pattern: /^[a-zA-Z0-9]*$/, message: t('Enter Valid SI No!') }]}>
                        <InputComponent placeholder={t('Enter Account Code')} disabled={viewDisable} />
                    </Form.Item>
                </Col>

                <Col xs={6}>
                    <Form.Item name="cc_type" label={t('Customer Type')} rules={[{ required: true, message: t('Please Select Customer Type!') }]}>
                        <Select placeholder={t('Enter Customer Type')} disabled={viewDisable}>
                            <Option value="PYR">{t('PAYER')}</Option>
                            <Option value="TPA">{t('TPA')}</Option>
                        </Select>
                    </Form.Item>
                </Col>

                <Col xs={6}>
                    <Form.Item name="cc_active" label={t('Active')} initialValue="Y">
                        <Select disabled={viewDisable}>
                            <Option value="Y">{t('Yes')}</Option>
                            <Option value="N">{t('No')}</Option>
                        </Select>
                    </Form.Item>
                </Col>
            </Row>

            {/* ===== Row 6: Allow ID Card, Referrer, Remarks ===== */}
            <Row gutter={20}>
                <Col xs={8}>
                    <Form.Item
                        name="cc_otherdetail1"
                        label={t('Allow ID Card')}
                        rules={[{ required: true, message: t('Please Select ID Card Option!') }]}
                    >
                        <Select disabled={viewDisable}>
                            <Option value="MND">{t('Mandatory')}</Option>
                            <Option value="OPT">{t('Optional')}</Option>
                            <Option value="NAP">{t('Not Applicable')}</Option>
                        </Select>
                    </Form.Item>
                </Col>

                <Col xs={8}>
                    <Form.Item name="referrer" label={t('Referrer')} rules={[{ required: true, message: t('Please Select Referrer!') }]}>
                        <ReferenceSelect
                            label="refr_desc"
                            mode="nura-base"
                            model={Referrers}
                            disabled={viewDisable}
                            config={{
                                queries: [{ field: 'refr_active', value: 'Y' }],
                            }}
                            field="refr_code"
                        />
                    </Form.Item>
                </Col>

                <Col xs={8}>
                    <Form.Item
                        name="cc_remarks"
                        label={t('Enter Remarks')}
                        rules={[
                            { max: 250, message: t('Length cannot exceed 250 characters!') },
                            { pattern: /^[\w\s\S]*$/, message: t('Enter Valid Remarks!') },
                        ]}
                    >
                        <InputComponent placeholder={t('Enter Remarks')} disabled={viewDisable} />
                    </Form.Item>
                </Col>
            </Row>

            {/* ===== Editor ===== */}

            <Row gutter={20}>
                <Col xs={24}>
                    <Editor
                        tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
                        ref={editorRef}
                        value={content}
                        onInit={handleInit}
                        onEditorChange={(value, editor) => handleUpdate(value, editor)}
                        initialValue={initialValues.cc_styledcontent}
                        init={{
                            height: 500,
                            plugins: ['template searchreplace visualblocks code fullscreen', 'insertdatetime media table paste help wordcount'],
                            toolbar:
                                'template | undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help',
                            content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                        }}
                    />
                </Col>
            </Row>
        </Form>
    );
};

export default CorporateDetail;
